import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Loader2 } from "lucide-react";
import { api, formatApiError, slugify } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ImageManager } from "@/pages/admin/ImageManager";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { SpanishFields, cleanEs } from "@/pages/admin/SpanishFields";

const ES_FIELDS = [
  { key: "title", label: "Título", type: "input" }, { key: "price_label", label: "Precio", type: "input" },
  { key: "location", label: "Ubicación", type: "input" }, { key: "tagline", label: "Resumen corto", rows: 3 },
  { key: "specs", label: "Datos clave (uno por línea)", type: "lines", rows: 5 }, { key: "description", label: "Descripción completa", rows: 12 },
  { key: "highlights", label: "Características (una por línea)", type: "lines", rows: 6 }, { key: "cta_label", label: "Texto del botón", type: "input" },
  { key: "seo_title", label: "Título SEO", type: "input" }, { key: "seo_description", label: "Descripción SEO", rows: 2 },
];

const blank = (kind) => ({
  slug: "", kind, category: kind === "rent" ? "stay" : "home", title: "", price_label: "", tagline: "", location: "Escamequita, Nicaragua",
  specs: [], highlights: [], description: "", cover_image: "", images: [], cta_label: kind === "rent" ? "Check Availability" : "WhatsApp Blue",
  external_url: "", website_url: "", video_url: "", price_tier: "", featured: false, order: 0, published: true, seo_title: "", seo_description: "", es: {},
});

const Field = ({ label, hint, children }) => (
  <div className="space-y-2">
    <Label className="font-bold text-ink">{label}</Label>
    {children}
    {hint && <p className="text-xs text-moss">{hint}</p>}
  </div>
);

const Section = ({ title, children }) => (
  <div className="space-y-5 rounded-2xl border border-clay bg-white p-6 sm:p-8">
    <h2 className="text-xl font-black text-forest">{title}</h2>
    {children}
  </div>
);

const selectCls = "h-10 w-full rounded-md border border-input bg-white px-3 text-sm font-medium";

export default function ListingEditor() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const isNew = id === "new";
  const nav = useNavigate();
  const [f, setF] = useState(isNew ? blank(params.get("kind") || "sale") : null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isNew) {
      api.get(`/admin/listings/${id}`).then((r) => setF(r.data)).catch((e) => toast.error(formatApiError(e)));
    }
  }, [id, isNew]);

  if (!f) return <p className="text-moss">Loading…</p>;
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const setLines = (k) => (e) => setF({ ...f, [k]: e.target.value.split("\n") });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = {
      ...f,
      slug: slugify(f.slug || f.title),
      specs: f.specs.map((s) => s.trim()).filter(Boolean),
      highlights: f.highlights.map((s) => s.trim()).filter(Boolean),
      cover_image: f.images[0] || "",
      order: Number(f.order) || 0,
      es: cleanEs(f.es),
    };
    try {
      if (isNew) await api.post("/admin/listings", body);
      else await api.put(`/admin/listings/${id}`, body);
      toast.success("Listing saved");
      nav(`/admin/listings?kind=${body.kind}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const rent = f.kind === "rent";
  return (
    <form onSubmit={save} className="max-w-4xl space-y-6" data-testid="listing-editor">
      <Link to={`/admin/listings?kind=${f.kind}`} className="inline-flex items-center gap-1 text-sm font-bold text-moss hover:text-forest"><ChevronLeft className="h-4 w-4" /> Back</Link>
      <AdminHeader title={isNew ? "New listing" : `Edit: ${f.title}`}>
        <button type="submit" disabled={saving} className="btn-primary" data-testid="listing-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save listing</button>
      </AdminHeader>

      <Section title="Basics">
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Title"><Input required value={f.title} onChange={set("title")} data-testid="listing-title" /></Field>
          <Field label="URL slug" hint={`Page address: ${rent ? "/for-rent/" : "/"}${slugify(f.slug || f.title) || "…"}`}>
            <Input value={f.slug} onChange={set("slug")} placeholder="auto from title" data-testid="listing-slug" />
          </Field>
          <Field label="Type">
            <select className={selectCls} value={f.kind} onChange={set("kind")} data-testid="listing-kind">
              <option value="sale">For sale</option><option value="rent">Place to stay</option>
            </select>
          </Field>
          <Field label="Category">
            <select className={selectCls} value={f.category} onChange={set("category")} data-testid="listing-category">
              <option value="home">Home</option><option value="ranch">Ranch & Estate</option><option value="land">Land / Lot</option><option value="stay">Stay</option>
            </select>
          </Field>
          <Field label={rent ? "Price label (optional)" : "Price"}><Input value={f.price_label} onChange={set("price_label")} placeholder="$429,000" data-testid="listing-price" /></Field>
          <Field label="Nightly price tier">
            <select className={selectCls} value={f.price_tier} onChange={set("price_tier")} data-testid="listing-tier">
              <option value="">—</option><option value="$">$ (&lt; 50)</option><option value="$$">$$ (&lt; 100)</option><option value="$$$">$$$ (&gt; 100)</option>
            </select>
          </Field>
          <Field label="Location"><Input value={f.location} onChange={set("location")} data-testid="listing-location" /></Field>
          <Field label="Sort order" hint="Lower numbers show first"><Input type="number" value={f.order} onChange={set("order")} data-testid="listing-order" /></Field>
        </div>
        <div className="flex flex-wrap gap-8">
          <label className="flex items-center gap-3 font-bold"><Switch checked={f.published} onCheckedChange={set("published")} data-testid="listing-published" /> Published</label>
          <label className="flex items-center gap-3 font-bold"><Switch checked={f.featured} onCheckedChange={set("featured")} data-testid="listing-featured" /> Featured on homepage</label>
        </div>
      </Section>

      <Section title="Photos"><ImageManager images={f.images} onChange={set("images")} /></Section>

      <Section title="Details">
        <Field label="Short summary (shown on cards)"><Textarea rows={3} value={f.tagline} onChange={set("tagline")} data-testid="listing-tagline" /></Field>
        <Field label="Key facts" hint="One per line, e.g. 3 bed / 2.5 bath"><Textarea rows={5} value={f.specs.join("\n")} onChange={setLines("specs")} data-testid="listing-specs" /></Field>
        <Field label="Full description" hint='Blank line = new paragraph. Start a line with "## " for a heading, "- " for a bullet, wrap **bold** text.'>
          <Textarea rows={14} value={f.description} onChange={set("description")} data-testid="listing-description" />
        </Field>
        <Field label="Property highlights" hint="One per line"><Textarea rows={6} value={f.highlights.join("\n")} onChange={setLines("highlights")} data-testid="listing-highlights" /></Field>
      </Section>

      <Section title="Buttons & links">
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Main button label"><Input value={f.cta_label} onChange={set("cta_label")} data-testid="listing-cta-label" /></Field>
          <Field label="Main button link" hint="Airbnb / booking link or WhatsApp link"><Input value={f.external_url} onChange={set("external_url")} data-testid="listing-cta-url" /></Field>
          <Field label="Website link (optional)"><Input value={f.website_url} onChange={set("website_url")} data-testid="listing-website" /></Field>
          <Field label="YouTube video link (optional)"><Input value={f.video_url} onChange={set("video_url")} data-testid="listing-video" /></Field>
        </div>
      </Section>

      <Section title="SEO">
        <Field label="SEO title" hint="Shown in Google results and browser tab"><Input value={f.seo_title} onChange={set("seo_title")} data-testid="listing-seo-title" /></Field>
        <Field label="SEO description" hint="~155 characters"><Textarea rows={3} value={f.seo_description} onChange={set("seo_description")} data-testid="listing-seo-description" /></Field>
      </Section>

      <SpanishFields fields={ES_FIELDS} source={f} value={f.es} onChange={(es) => setF({ ...f, es })} testId="listing-es" />
    </form>
  );
}
